import { AWARD_TYPE_QK } from '@admin/engagement/awards/configure/useAwardTypeDataAndMutations'
import { useApiActionToast } from '@globals/toaster/useToasterStore'
import { createAward, CreateAwardParams, createDelegationAward } from '@kudos/http-client'
import { useApiError } from '@shared/errors/useApiError'
import { useInvalidateQuery } from '@shared/hooks/useInvalidateQuery'
import { getSendToFromTenantMemberAndTeams } from '@shared/utils/getSendToFromTenantMemberAndTeams'
import { getQueryKeysForEntity } from '@shared/wallPost/context/timelineQueryKeys'
import { useMutation } from '@tanstack/react-query'
import { useT } from '@transifex/react'
import { UseFormSetError } from 'react-hook-form'
import { useAwardsRouteContext } from '../useAwardsRouteContext'
import { SendAwardFormData } from './useSendAwardForm'

const useSendAwardMutations = (
  awardTypeId: string,
  setError: UseFormSetError<SendAwardFormData>,
) => {
  const { delegatorId } = useAwardsRouteContext()
  const invalidateQuery = useInvalidateQuery()
  const t = useT()
  const { handleApiError } = useApiError<SendAwardFormData>()
  const [showSuccess] = useApiActionToast()
  const { mutateAsync: createAwardMutation, isPending } = useMutation({
    mutationFn: (params: CreateAwardParams) => {
      if (delegatorId) {
        return createDelegationAward(delegatorId, params, awardTypeId)
      }
      return createAward(params, awardTypeId)
    },
  })

  const publishAward = (data: SendAwardFormData, resetAwardForm: () => void) => {
    return createAwardMutation(
      {
        amount: data.amount,
        message: data.message,
        sendTo: getSendToFromTenantMemberAndTeams(data.sendTo),
        qualityIds: data.qualities.map(({ id }) => id),
        refinementId: data.refinementId ?? null,
      },
      {
        onSuccess: async () => {
          resetAwardForm()
          await invalidateQuery([AWARD_TYPE_QK, awardTypeId])
          // A new award belongs in the feeds, in the recipient's badge summary
          // and in the profile summary tiles, the same surfaces that go stale
          // when an existing award changes.
          await Promise.all(
            getQueryKeysForEntity('award').map(queryKey => invalidateQuery(queryKey)),
          )
          if (delegatorId) {
            await invalidateQuery(['delegatorPoints', delegatorId])
          }
          showSuccess(t('Award sent successfully'))
        },
        onError: error => handleApiError(error, setError, ['amount', 'message', 'sendTo']),
      },
    )
  }

  return { publishAward, isPending }
}

export default useSendAwardMutations
